import React, { useState, useEffect, useCallback } from "react";
import {
  UserPlus,
  Phone,
  Mail,
  Calendar,
  Tag,
  StickyNote,
  MessageSquare,
  ExternalLink,
  Loader2,
  X,
  Plus,
  ChevronRight,
  Search,
  Filter,
  Check,
  PhoneCall,
  Star,
  TrendingUp,
  XCircle,
  MoreVertical,
  Edit3,
  Trash2,
  Smile,
  Send,
  AlertCircle,
  Clock,
  Facebook,
} from "lucide-react";
import {
  fetchClaw9Accounts,
  fetchClaw9Leads,
  fetchClaw9LeadStats,
  createClaw9Lead,
  updateClaw9Lead,
  fetchClaw9Lead,
  addLeadNote,
  editLeadNote,
  deleteLeadNote,
  toggleNoteReaction,
  sendZaloMessageToLead,
} from "../api/claw9";
import { getChannelLabel } from "../utils/messagingChannels";

// Pipeline stages configuration - Claw9 red theme
const PIPELINE_STAGES = [
  { id: "new", label: "New", color: "red", icon: UserPlus },
  { id: "contacted", label: "Contacted", color: "orange", icon: PhoneCall },
  { id: "qualified", label: "Qualified", color: "amber", icon: Star },
  { id: "negotiating", label: "Negotiating", color: "purple", icon: TrendingUp },
  { id: "converted", label: "Converted", color: "emerald", icon: Check },
  { id: "lost", label: "Lost", color: "slate", icon: XCircle },
];

// Source configuration
const SOURCES = {
  zalo: { label: "Zalo", color: "blue" },
  telegram: { label: "Telegram", color: "sky" },
  messenger: { label: "Facebook Messenger", color: "indigo" },
  facebook: { label: "Facebook Messenger", color: "indigo" },
  instagram: { label: "Instagram DM", color: "purple" },
  whatsapp: { label: "WhatsApp", color: "emerald" },
  manual: { label: "Manual", color: "slate" },
  website: { label: "Website", color: "purple" },
  referral: { label: "Referral", color: "amber" },
};

// Note type configuration
const NOTE_TYPES = {
  general: { label: "General", icon: StickyNote, color: "slate" },
  contact: { label: "Contact", icon: Phone, color: "blue" },
  follow_up: { label: "Follow Up", icon: Clock, color: "amber" },
  status_change: { label: "Status Change", icon: AlertCircle, color: "purple" },
};

// Common emoji reactions
const EMOJI_REACTIONS = ["👍", "👎", "❤️", "🎉", "😄", "🤔", "👀", "🚀", "✅", "⚠️"];

// Helper to format date
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

// Source badge component
const SourceBadge = ({ source }) => {
  const config = SOURCES[source] || { label: getChannelLabel(source), color: "slate" };
  const colorClasses = {
    blue: "bg-blue-500/20 text-blue-400",
    sky: "bg-sky-500/20 text-sky-400",
    indigo: "bg-indigo-500/20 text-indigo-400",
    purple: "bg-purple-500/20 text-purple-400",
    emerald: "bg-emerald-500/20 text-emerald-400",
    slate: "bg-white/10 text-white/60",
    amber: "bg-amber-500/20 text-amber-400",
  };

  return (
    <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${colorClasses[config.color]}`}>
      {config.label}
    </span>
  );
};

// Emoji Picker Component
const EmojiPicker = ({ onSelect, onClose }) => {
  return (
    <div className="absolute bottom-full left-0 mb-2 p-2 bg-[#1a2235] border border-white/10 rounded-xl shadow-xl z-10">
      <div className="flex flex-wrap gap-1 max-w-[200px]">
        {EMOJI_REACTIONS.map((emoji) => (
          <button
            key={emoji}
            onClick={() => {
              onSelect(emoji);
              onClose();
            }}
            className="w-8 h-8 flex items-center justify-center text-lg hover:bg-white/10 rounded-lg transition-colors"
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
};

// Note Item Component
const NoteItem = ({ note, noteIndex, leadId, currentUserEmail, onUpdate, onRefresh }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(note.note || "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const noteConfig = NOTE_TYPES[note.note_type] || NOTE_TYPES.general;
  const NoteIcon = noteConfig.icon;
  const isOwnNote = note.created_by === currentUserEmail;

  const noteColorClasses = {
    slate: "border-white/10 bg-white/5",
    blue: "border-blue-500/30 bg-blue-500/5",
    amber: "border-amber-500/30 bg-amber-500/5",
    purple: "border-purple-500/30 bg-purple-500/5",
  };

  const handleEdit = async () => {
    if (!editContent.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await editLeadNote(leadId, noteIndex, undefined, { note: editContent.trim() });
      setIsEditing(false);
      onRefresh?.();
    } catch (error) {
      console.error("Failed to edit note:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await deleteLeadNote(leadId, noteIndex, undefined, note.timestamp);
      setShowMenu(false);
      onRefresh?.();
    } catch (error) {
      console.error("Failed to delete note:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReaction = async (emoji) => {
    try {
      await toggleNoteReaction(leadId, noteIndex, undefined, emoji);
      onRefresh?.();
    } catch (error) {
      console.error("Failed to toggle reaction:", error);
    }
  };

  // Format timestamp
  const formatNoteTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  // Get creator initials
  const getCreatorInitials = () => {
    if (note.created_by_name) {
      return note.created_by_name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
    }
    if (note.created_by) {
      return note.created_by.slice(0, 2).toUpperCase();
    }
    return "??";
  };

  return (
    <div className={`p-3 rounded-lg border ${noteColorClasses[noteConfig.color]} relative group`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#ff4d4d]/30 to-[#ff6b6b]/20 flex items-center justify-center text-[10px] font-medium text-white/80">
            {getCreatorInitials()}
          </div>
          <div>
            <p className="text-xs font-medium text-white/80">
              {note.created_by_name || note.created_by || "Unknown"}
            </p>
            <div className="flex items-center gap-1.5 text-[10px] text-white/40">
              <NoteIcon className="w-3 h-3" />
              <span>{noteConfig.label}</span>
              <span>·</span>
              <span>{formatNoteTime(note.timestamp)}</span>
              {note.edited && <span className="italic">(edited)</span>}
            </div>
          </div>
        </div>

        {/* Menu button - only show for own notes */}
        {isOwnNote && (
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1 text-white/40 hover:text-white hover:bg-white/10 rounded opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {showMenu && (
              <div className="absolute right-0 top-full mt-1 w-32 bg-[#1a2235] border border-white/10 rounded-lg shadow-xl overflow-hidden z-20">
                <button
                  onClick={() => {
                    setIsEditing(true);
                    setShowMenu(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white/70 hover:bg-white/10"
                >
                  <Edit3 className="w-3 h-3" />
                  Edit
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isSubmitting}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:bg-red-500/10 disabled:opacity-50"
                >
                  <Trash2 className="w-3 h-3" />
                  Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      {isEditing ? (
        <div className="space-y-2">
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="w-full px-2 py-1.5 text-sm bg-white/5 border border-white/10 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white placeholder-white/30"
            rows={3}
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setIsEditing(false);
                setEditContent(note.note || "");
              }}
              className="px-2 py-1 text-xs text-white/50 hover:bg-white/5 rounded"
            >
              Cancel
            </button>
            <button
              onClick={handleEdit}
              disabled={!editContent.trim() || isSubmitting}
              className="px-2 py-1 text-xs bg-[#ff4d4d] text-white rounded disabled:opacity-50"
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-white/80 whitespace-pre-wrap">{note.note}</p>
      )}

      {/* Mentioned users */}
      {note.mentioned_users?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {note.mentioned_users.map((user, idx) => (
            <span key={idx} className="px-1.5 py-0.5 text-[10px] bg-[#ff4d4d]/20 text-[#ff4d4d] rounded">
              @{user.name || user.email}
            </span>
          ))}
        </div>
      )}

      {/* Reactions */}
      <div className="flex items-center gap-1 mt-2 pt-2 border-t border-white/5">
        {/* Existing reactions */}
        {note.reactions && Object.entries(note.reactions).map(([emoji, users]) => (
          users.length > 0 && (
            <button
              key={emoji}
              onClick={() => handleReaction(emoji)}
              className={`flex items-center gap-1 px-1.5 py-0.5 text-xs rounded-full transition-colors ${
                users.includes(currentUserEmail)
                  ? "bg-[#ff4d4d]/20 text-[#ff4d4d]"
                  : "bg-white/5 text-white/60 hover:bg-white/10"
              }`}
              title={users.join(", ")}
            >
              <span>{emoji}</span>
              <span className="text-[10px]">{users.length}</span>
            </button>
          )
        ))}

        {/* Add reaction button */}
        <div className="relative">
          <button
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className="p-1 text-white/30 hover:text-white/60 hover:bg-white/5 rounded transition-colors"
          >
            <Smile className="w-3.5 h-3.5" />
          </button>
          {showEmojiPicker && (
            <EmojiPicker
              onSelect={handleReaction}
              onClose={() => setShowEmojiPicker(false)}
            />
          )}
        </div>
      </div>

      {/* Click outside to close menus */}
      {(showMenu || showEmojiPicker) && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => {
            setShowMenu(false);
            setShowEmojiPicker(false);
          }}
        />
      )}
    </div>
  );
};

// Notes List Component
const NotesList = ({ notes, leadId, currentUserEmail, onRefresh, onAddNote }) => {
  const sortedNotes = [...(notes || [])].sort((a, b) => {
    const timeA = new Date(a.timestamp || 0).getTime();
    const timeB = new Date(b.timestamp || 0).getTime();
    return timeB - timeA; // Newest first
  });

  return (
    <div className="space-y-3">
      {/* Add note button */}
      <button
        onClick={onAddNote}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 border border-dashed border-white/20 hover:border-[#ff4d4d]/50 rounded-lg text-sm text-white/60 hover:text-white transition-colors"
      >
        <Plus className="w-4 h-4" />
        Add Note
      </button>

      {/* Notes */}
      {sortedNotes.length > 0 ? (
        sortedNotes.map((note, index) => (
          <NoteItem
            key={note.timestamp || index}
            note={note}
            noteIndex={index}
            leadId={leadId}
            currentUserEmail={currentUserEmail}
            onRefresh={onRefresh}
          />
        ))
      ) : (
        <div className="text-center py-6 text-white/40 text-sm">
          No notes yet. Add a note to get started.
        </div>
      )}
    </div>
  );
};

// Add Note Modal Component
const AddNoteModal = ({ isOpen, onClose, leadId, onSuccess }) => {
  const [content, setContent] = useState("");
  const [noteType, setNoteType] = useState("general");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!content.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await addLeadNote(leadId, undefined, {
        note: content.trim(),
        note_type: noteType,
      });
      setContent("");
      setNoteType("general");
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Failed to add note:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      handleSubmit();
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-md mx-4 bg-[#0d1424] rounded-2xl shadow-2xl border border-white/10">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white">Add Note</h3>
          <button
            onClick={onClose}
            className="p-2 text-white/50 hover:text-white hover:bg-white/5 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Note type selector */}
          <div>
            <label className="block text-xs text-white/50 mb-2">Note Type</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(NOTE_TYPES).map(([type, config]) => {
                const Icon = config.icon;
                const isActive = noteType === type;
                return (
                  <button
                    key={type}
                    onClick={() => setNoteType(type)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      isActive
                        ? "bg-[#ff4d4d] text-white"
                        : "bg-white/5 text-white/60 hover:bg-white/10"
                    }`}
                  >
                    <Icon className="w-3 h-3" />
                    {config.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Note content */}
          <div>
            <label className="block text-xs text-white/50 mb-2">Content</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Write your note here..."
              rows={5}
              className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white placeholder-white/30"
              autoFocus
            />
            <p className="text-[10px] text-white/30 mt-1">
              Press Cmd/Ctrl + Enter to submit
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-white/60 hover:bg-white/5 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!content.trim() || isSubmitting}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-[#ff4d4d] hover:bg-[#ff6b6b] text-white rounded-lg disabled:opacity-50"
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            Add Note
          </button>
        </div>
      </div>
    </div>
  );
};

// Zalo icon component
const ZaloIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 17.703c-.063.063-.188.125-.313.125h-2.25c-.125 0-.25-.063-.313-.125l-2.25-2.25v2.125c0 .125-.063.25-.188.25h-1.5c-.125 0-.188-.125-.188-.25V8.172c0-.125.063-.25.188-.25h1.5c.125 0 .188.125.188.25v4.875l2.25-2.25c.063-.063.188-.125.313-.125h2.25c.125 0 .25.063.313.125.063.063.063.188 0 .313l-2.813 2.813 2.813 2.813c.063.125.063.25 0 .313v.004zm-7.894-1.2c0 .125-.063.25-.188.25h-1.5c-.125 0-.188-.125-.188-.25V8.172c0-.125.063-.25.188-.25h1.5c.125 0 .188.125.188.25v8.331z"/>
  </svg>
);

// Message templates
const MESSAGE_TEMPLATES = [
  {
    name: "Initial Contact",
    text: "Chào bạn! Cảm ơn bạn đã quan tâm đến dịch vụ của chúng tôi. Tôi có thể hỗ trợ bạn như thế nào?"
  },
  {
    name: "Follow Up",
    text: "Chào bạn, tôi muốn hỏi thăm về nhu cầu của bạn. Bạn có thời gian trao đổi không?"
  },
  {
    name: "Thank You",
    text: "Cảm ơn bạn đã tin tưởng sử dụng dịch vụ của chúng tôi. Nếu có bất kỳ câu hỏi nào, hãy liên hệ với tôi nhé!"
  }
];

// Send Zalo Message Modal Component
const SendZaloMessageModal = ({ isOpen, onClose, lead, onSuccess }) => {
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setMessage("");
      setError(null);
      setSuccess(false);
    }
  }, [isOpen]);

  if (!isOpen || !lead) return null;

  const handleSend = async () => {
    if (!message.trim() || isSending) return;

    setIsSending(true);
    setError(null);

    try {
      const result = await sendZaloMessageToLead(lead.id, message.trim());

      if (result.success) {
        setSuccess(true);
        setMessage("");
        onSuccess?.();
        // Auto close after success
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        // Handle specific errors
        if (result.error?.includes("does not have a Zalo user ID")) {
          setError("This lead hasn't been linked to Zalo yet. They need to message your Zalo account first.");
        } else if (result.error?.includes("not found")) {
          setError("Lead not found. It may have been deleted.");
        } else {
          setError(result.error || "Failed to send message. Please try again.");
        }
      }
    } catch (err) {
      console.error("Failed to send Zalo message:", err);
      setError("Failed to send message. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      handleSend();
    }
  };

  const handleTemplateSelect = (templateText) => {
    setMessage(templateText);
  };

  const charCount = message.length;
  const maxChars = 5000;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-md mx-4 bg-[#0d1424] rounded-2xl shadow-2xl border border-white/10">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
              <ZaloIcon />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Send Zalo Message</h3>
              <p className="text-xs text-white/50">To: {lead.name || lead.contact_name || "Unknown"}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/50 hover:text-white hover:bg-white/5 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Success message */}
          {success && (
            <div className="flex items-center gap-2 p-3 bg-emerald-500/20 border border-emerald-500/30 rounded-lg text-emerald-400">
              <Check className="w-4 h-4" />
              <span className="text-sm">Message sent successfully!</span>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Template selector */}
          <div>
            <label className="block text-xs text-white/50 mb-2">Quick Templates</label>
            <div className="flex flex-wrap gap-2">
              {MESSAGE_TEMPLATES.map((template) => (
                <button
                  key={template.name}
                  onClick={() => handleTemplateSelect(template.text)}
                  className="px-3 py-1.5 text-xs bg-white/5 text-white/60 rounded-lg hover:bg-white/10 transition-colors"
                >
                  {template.name}
                </button>
              ))}
            </div>
          </div>

          {/* Message input */}
          <div>
            <label className="block text-xs text-white/50 mb-2">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message here..."
              rows={5}
              maxLength={maxChars}
              className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-white/30"
              autoFocus
              disabled={isSending || success}
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-[10px] text-white/30">
                Press Cmd/Ctrl + Enter to send
              </p>
              <p className={`text-[10px] ${charCount > maxChars * 0.9 ? "text-amber-400" : "text-white/30"}`}>
                {charCount}/{maxChars}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-white/60 hover:bg-white/5 rounded-lg"
          >
            {success ? "Close" : "Cancel"}
          </button>
          {!success && (
            <button
              onClick={handleSend}
              disabled={!message.trim() || isSending}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Send Message
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Stats card component - Claw9 red theme
const StatsCard = ({ stage, count, isActive, onClick }) => {
  const config = PIPELINE_STAGES.find((s) => s.id === stage) || PIPELINE_STAGES[0];
  const Icon = config.icon;

  const colorClasses = {
    red: "bg-[#ff4d4d]/10 text-[#ff4d4d] border-[#ff4d4d]/30",
    orange: "bg-orange-500/10 text-orange-400 border-orange-500/30",
    amber: "bg-amber-500/10 text-amber-400 border-amber-500/30",
    purple: "bg-purple-500/10 text-purple-400 border-purple-500/30",
    emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    slate: "bg-white/5 text-white/60 border-white/10",
  };

  return (
    <button
      onClick={onClick}
      className={`p-3 rounded-xl border transition-all ${colorClasses[config.color]} ${
        isActive ? "ring-2 ring-offset-2 ring-[#ff4d4d] ring-offset-[#0a0f1c]" : "hover:shadow-md"
      }`}
    >
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <span className="text-2xl font-bold">{count || 0}</span>
      </div>
      <p className="text-xs mt-1 opacity-80">{config.label}</p>
    </button>
  );
};

// Lead card component for Kanban
const LeadCard = ({ lead, onClick, onDragStart, onDragEnd }) => {
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, lead)}
      onDragEnd={onDragEnd}
      onClick={() => onClick(lead)}
      className="p-3 bg-[#0d1424] rounded-lg border border-white/10 shadow-sm hover:shadow-md hover:border-[#ff4d4d]/30 cursor-pointer transition-all group"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#ff4d4d]/30 to-[#ff6b6b]/20 flex items-center justify-center text-xs font-medium text-white/80 flex-shrink-0">
            {(lead.name || lead.contact_name)?.[0]?.toUpperCase() || "?"}
          </div>
          <div className="min-w-0">
            <p className="font-medium text-sm text-white truncate">
              {lead.name || lead.contact_name || "Unknown"}
            </p>
            {lead.phone && (
              <p className="text-xs text-white/50 truncate">
                {lead.phone}
              </p>
            )}
          </div>
        </div>
        <SourceBadge source={lead.source} />
      </div>

      {/* Tags */}
      {lead.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {lead.tags.slice(0, 3).map((tag, idx) => (
            <span
              key={idx}
              className="px-1.5 py-0.5 text-[10px] bg-white/5 text-white/50 rounded"
            >
              {tag}
            </span>
          ))}
          {lead.tags.length > 3 && (
            <span className="text-[10px] text-white/40">+{lead.tags.length - 3}</span>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
        <span className="text-[10px] text-white/40">
          {lead.last_contact_at ? formatDate(lead.last_contact_at) : "No contact"}
        </span>
        <ChevronRight className="w-3 h-3 text-white/40 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </div>
  );
};

// Kanban column component - Claw9 red theme
const KanbanColumn = ({
  stage,
  leads,
  onLeadClick,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}) => {
  const config = PIPELINE_STAGES.find((s) => s.id === stage) || PIPELINE_STAGES[0];
  const Icon = config.icon;

  const headerColorClasses = {
    red: "text-[#ff4d4d] bg-[#ff4d4d]/10",
    orange: "text-orange-400 bg-orange-500/10",
    amber: "text-amber-400 bg-amber-500/10",
    purple: "text-purple-400 bg-purple-500/10",
    emerald: "text-emerald-400 bg-emerald-500/10",
    slate: "text-white/60 bg-white/5",
  };

  return (
    <div
      className="flex-shrink-0 w-72 h-full flex flex-col bg-[#0a0f1c] rounded-xl border border-white/10 shadow-sm"
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, stage)}
    >
      {/* Column Header */}
      <div className={`p-3 rounded-t-xl ${headerColorClasses[config.color]}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4" />
            <span className="font-semibold text-sm">{config.label}</span>
          </div>
          <span className="px-2 py-0.5 text-xs font-medium bg-black/20 rounded-full">
            {leads.length}
          </span>
        </div>
      </div>

      {/* Cards */}
      <div className="flex-1 min-h-0 p-2 space-y-2 overflow-y-auto">
        {leads.map((lead) => (
          <LeadCard
            key={lead.id}
            lead={lead}
            onClick={onLeadClick}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          />
        ))}
        {leads.length === 0 && (
          <div className="p-4 text-center text-sm text-white/40">
            No leads
          </div>
        )}
      </div>
    </div>
  );
};

// Lead detail modal component - Claw9 red theme
const LeadDetailModal = ({ lead, isOpen, onClose, onUpdate, onNavigateToChat, currentUserEmail, onRefreshLead }) => {
  const [editData, setEditData] = useState({ tags: [] });
  const [isSaving, setIsSaving] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [isSendMessageModalOpen, setIsSendMessageModalOpen] = useState(false);

  useEffect(() => {
    if (lead) {
      setEditData({
        tags: lead.tags || [],
      });
    }
  }, [lead]);

  if (!isOpen || !lead) return null;

  const handleRefreshNotes = async () => {
    onRefreshLead?.(lead.id);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onUpdate(lead.id, editData);
    } finally {
      setIsSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setIsSaving(true);
    try {
      await onUpdate(lead.id, { status: newStatus });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddTag = () => {
    const tags = editData.tags || [];
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setEditData((prev) => ({
        ...prev,
        tags: [...(prev.tags || []), newTag.trim()],
      }));
      setNewTag("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setEditData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tagToRemove),
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-lg mx-4 bg-[#0d1424] rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden border border-white/10">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#ff4d4d]/30 to-[#ff6b6b]/20 flex items-center justify-center text-lg font-medium text-white/80">
              {(lead.name || lead.contact_name)?.[0]?.toUpperCase() || "?"}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                {lead.name || lead.contact_name || "Unknown Lead"}
              </h2>
              <div className="flex items-center gap-2 mt-0.5">
                <SourceBadge source={lead.source} />
                <span className="text-xs text-white/50">
                  Created {formatDate(lead.created_at)}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/50 hover:text-white hover:bg-white/5 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Status Actions */}
          <div className="mb-6">
            <p className="text-xs text-white/50 mb-2">Pipeline Stage</p>
            <div className="flex flex-wrap gap-2">
              {PIPELINE_STAGES.filter((s) => s.id !== "lost").map((stage) => {
                const Icon = stage.icon;
                const isActive = lead.status === stage.id;
                return (
                  <button
                    key={stage.id}
                    onClick={() => handleStatusChange(stage.id)}
                    disabled={isSaving}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      isActive
                        ? "bg-[#ff4d4d] text-white"
                        : "bg-white/5 text-white/60 hover:bg-white/10"
                    } disabled:opacity-50`}
                  >
                    <Icon className="w-3 h-3" />
                    {stage.label}
                  </button>
                );
              })}
              <button
                onClick={() => handleStatusChange("lost")}
                disabled={isSaving}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  lead.status === "lost"
                    ? "bg-red-600 text-white"
                    : "bg-red-500/10 text-red-400 hover:bg-red-500/20"
                } disabled:opacity-50`}
              >
                <XCircle className="w-3 h-3" />
                Lost
              </button>
            </div>
          </div>

          {/* Contact Info */}
          <div className="mb-6 space-y-3">
            <p className="text-xs text-white/50">Contact Information</p>
            {lead.phone && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-white/50" />
                </div>
                <p className="text-sm text-white">{lead.phone}</p>
              </div>
            )}
            {lead.email && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-white/50" />
                </div>
                <p className="text-sm text-white">{lead.email}</p>
              </div>
            )}
            {lead.last_contact_at && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-white/50" />
                </div>
                <div>
                  <p className="text-xs text-white/50">Last Contact</p>
                  <p className="text-sm text-white">
                    {new Date(lead.last_contact_at).toLocaleString()}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Tags */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Tag className="w-4 h-4 text-white/50" />
              <span className="text-xs text-white/50">Tags</span>
            </div>
            <div className="flex flex-wrap gap-1 mb-2">
              {(editData.tags || []).map((tag, idx) => (
                <span
                  key={idx}
                  className="px-2 py-0.5 text-xs bg-white/10 text-white/70 rounded-full flex items-center gap-1"
                >
                  {tag}
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    className="text-white/40 hover:text-white"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddTag()}
                placeholder="Add tag..."
                className="flex-1 px-3 py-1.5 text-sm bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white placeholder-white/30"
              />
              <button
                onClick={handleAddTag}
                className="px-3 py-1.5 text-xs bg-white/5 text-white/60 rounded-lg hover:bg-white/10"
              >
                Add
              </button>
            </div>
          </div>

          {/* Structured Notes */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <StickyNote className="w-4 h-4 text-white/50" />
              <span className="text-xs text-white/50">
                Notes ({lead.structured_notes?.length || 0})
              </span>
            </div>
            <NotesList
              notes={lead.structured_notes}
              leadId={lead.id}
              currentUserEmail={currentUserEmail}
              onRefresh={handleRefreshNotes}
              onAddNote={() => setIsAddNoteModalOpen(true)}
            />
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            {/* Send Zalo Message - show for Zalo leads */}
            {(lead.source === 'zalo' || lead.zalo_user_id) && (
              <button
                onClick={() => setIsSendMessageModalOpen(true)}
                className="w-full flex items-center justify-between p-3 bg-blue-500/10 rounded-lg text-blue-400 hover:bg-blue-500/20 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <ZaloIcon />
                  <span className="text-sm font-medium">Send Zalo Message</span>
                </div>
                <Send className="w-4 h-4" />
              </button>
            )}

            {/* Conversation Link */}
            {lead.thread_id && (
              <button
                onClick={() => onNavigateToChat?.(lead)}
                className="w-full flex items-center justify-between p-3 bg-[#ff4d4d]/10 rounded-lg text-[#ff4d4d] hover:bg-[#ff4d4d]/20 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  <span className="text-sm font-medium">View Conversation</span>
                </div>
                <ExternalLink className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-white/60 hover:bg-white/5 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 text-sm bg-[#ff4d4d] hover:bg-[#ff6b6b] text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Save Changes
          </button>
        </div>
      </div>

      {/* Add Note Modal */}
      <AddNoteModal
        isOpen={isAddNoteModalOpen}
        onClose={() => setIsAddNoteModalOpen(false)}
        leadId={lead.id}
        onSuccess={handleRefreshNotes}
      />

      {/* Send Zalo Message Modal */}
      <SendZaloMessageModal
        isOpen={isSendMessageModalOpen}
        onClose={() => setIsSendMessageModalOpen(false)}
        lead={lead}
        onSuccess={handleRefreshNotes}
      />
    </div>
  );
};

// Create lead modal component - Claw9 red theme
const CreateLeadModal = ({ isOpen, onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    source: "manual",
    notes: "",
    tags: [],
  });
  const [isCreating, setIsCreating] = useState(false);
  const [newTag, setNewTag] = useState("");

  const handleCreate = async () => {
    if (!formData.name.trim()) return;

    setIsCreating(true);
    try {
      await onCreate(formData);
      setFormData({
        name: "",
        phone: "",
        email: "",
        source: "manual",
        notes: "",
        tags: [],
      });
      onClose();
    } finally {
      setIsCreating(false);
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }));
      setNewTag("");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-md mx-4 bg-[#0d1424] rounded-2xl shadow-2xl border border-white/10">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">Create New Lead</h2>
          <button
            onClick={onClose}
            className="p-2 text-white/50 hover:text-white hover:bg-white/5 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-xs text-white/50 mb-1">
              Name <span className="text-[#ff4d4d]">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Enter lead name"
              className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white placeholder-white/30"
            />
          </div>

          <div>
            <label className="block text-xs text-white/50 mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
              placeholder="+84 xxx xxx xxx"
              className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white placeholder-white/30"
            />
          </div>

          <div>
            <label className="block text-xs text-white/50 mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
              placeholder="email@example.com"
              className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white placeholder-white/30"
            />
          </div>

          <div>
            <label className="block text-xs text-white/50 mb-1">Source</label>
            <select
              value={formData.source}
              onChange={(e) => setFormData((prev) => ({ ...prev, source: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white"
            >
              {Object.entries(SOURCES).filter(([key]) => key !== "facebook").map(([key, config]) => (
                <option key={key} value={key} className="bg-[#0d1424]">
                  {config.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-white/50 mb-1">Tags</label>
            <div className="flex flex-wrap gap-1 mb-2">
              {formData.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-2 py-0.5 text-xs bg-white/10 text-white/70 rounded-full flex items-center gap-1"
                >
                  {tag}
                  <button
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        tags: prev.tags.filter((t) => t !== tag),
                      }))
                    }
                    className="text-white/40 hover:text-white"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddTag()}
                placeholder="Add tag..."
                className="flex-1 px-3 py-1.5 text-sm bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white placeholder-white/30"
              />
              <button
                onClick={handleAddTag}
                className="px-3 py-1.5 text-xs bg-white/5 text-white/60 rounded-lg hover:bg-white/10"
              >
                Add
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs text-white/50 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
              placeholder="Add notes..."
              rows={3}
              className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white placeholder-white/30"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-white/60 hover:bg-white/5 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!formData.name.trim() || isCreating}
            className="px-4 py-2 text-sm bg-[#ff4d4d] hover:bg-[#ff6b6b] text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create Lead
          </button>
        </div>
      </div>
    </div>
  );
};

// Main CRM Component - Claw9 red theme
const Claw9CRM = ({ userEmail, onNavigate, onOpenMessagingChannels }) => {
  // Account state - fetched dynamically
  const [accountId, setAccountId] = useState(null);
  const [isLoadingAccount, setIsLoadingAccount] = useState(true);

  // State
  const [stats, setStats] = useState({});
  const [leads, setLeads] = useState([]);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [isLoadingLeads, setIsLoadingLeads] = useState(true);

  // Filters
  const [sourceFilter, setSourceFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Modal state
  const [selectedLead, setSelectedLead] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Drag state
  const [draggedLead, setDraggedLead] = useState(null);

  // Fetch account on mount
  useEffect(() => {
    const loadAccount = async () => {
      try {
        const result = await fetchClaw9Accounts();
        const accounts = result.accounts || [];
        if (accounts.length > 0) {
          setAccountId(accounts[0].account_id);
        } else {
          console.warn('[Claw9CRM] No accounts found');
        }
      } catch (error) {
        console.error('[Claw9CRM] Failed to load accounts:', error);
      } finally {
        setIsLoadingAccount(false);
      }
    };
    loadAccount();
  }, []);

  // Load stats
  const loadStats = useCallback(async () => {
    if (!accountId) return;
    try {
      const result = await fetchClaw9LeadStats(accountId);
      const statsData = result.stats || result.data?.stats || result.data || result || {};
      setStats(statsData);
    } catch (error) {
      console.error("[Claw9CRM] Failed to load stats:", error);
    } finally {
      setIsLoadingStats(false);
    }
  }, [accountId]);

  // Load leads
  const loadLeads = useCallback(async () => {
    if (!accountId) return;
    setIsLoadingLeads(true);
    try {
      const result = await fetchClaw9Leads({
        accountId,
        source: sourceFilter !== "all" ? sourceFilter : undefined,
      });
      const leadsData = result.leads || result.data?.leads || result.data || [];
      setLeads(Array.isArray(leadsData) ? leadsData : []);
    } catch (error) {
      console.error("[Claw9CRM] Failed to load leads:", error);
    } finally {
      setIsLoadingLeads(false);
    }
  }, [accountId, sourceFilter]);

  // Load data when account is ready
  useEffect(() => {
    if (accountId) {
      loadStats();
      loadLeads();
    }
  }, [accountId, loadStats, loadLeads]);

  // Reload on filter change
  useEffect(() => {
    if (accountId) {
      loadLeads();
    }
  }, [sourceFilter, accountId, loadLeads]);

  // Group leads by status
  const leadsByStatus = PIPELINE_STAGES.reduce((acc, stage) => {
    acc[stage.id] = leads.filter((lead) => lead.status === stage.id);
    return acc;
  }, {});

  // Filter leads by search
  const filterLeads = (leadsArray) => {
    if (!searchTerm) return leadsArray;
    const term = searchTerm.toLowerCase();
    return leadsArray.filter(
      (lead) =>
        lead.name?.toLowerCase().includes(term) ||
        lead.phone?.includes(term) ||
        lead.email?.toLowerCase().includes(term)
    );
  };

  // Handle lead click
  const handleLeadClick = async (lead) => {
    try {
      const fullLead = await fetchClaw9Lead(lead.id, accountId);
      setSelectedLead(fullLead);
      setIsDetailModalOpen(true);
    } catch (error) {
      setSelectedLead(lead);
      setIsDetailModalOpen(true);
    }
  };

  // Handle lead update
  const handleUpdateLead = async (leadId, data) => {
    try {
      const updated = await updateClaw9Lead(leadId, accountId, data);

      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, ...updated } : l)));
      setSelectedLead((prev) => (prev?.id === leadId ? { ...prev, ...updated } : prev));

      if (data.status) {
        loadStats();
      }
    } catch (error) {
      console.error("Failed to update lead:", error);
      throw error;
    }
  };

  // Handle create lead
  const handleCreateLead = async (data) => {
    try {
      const created = await createClaw9Lead(accountId, data);
      setLeads((prev) => [created, ...prev]);
      loadStats();
    } catch (error) {
      console.error("Failed to create lead:", error);
      throw error;
    }
  };

  // Drag handlers
  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setDraggedLead(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    if (!draggedLead || draggedLead.status === newStatus) return;

    try {
      await handleUpdateLead(draggedLead.id, { status: newStatus });
    } catch (error) {
      console.error("Failed to update lead status:", error);
    }
  };

  // Navigate to chat
  const handleNavigateToChat = (lead) => {
    if (onNavigate && lead.thread_id) {
      onNavigate("claw9-chat", { threadId: lead.thread_id, channel: lead.source });
    }
  };

  return (
    <div className="flex flex-1 flex-col min-h-0 h-full bg-[#050810] border border-white/10 rounded-xl overflow-hidden shadow-2xl relative">
      {/* Header */}
      <div className="p-4 lg:p-6 bg-[#0a0f1c] border-b border-white/10">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-white">Customers</h1>
            <p className="text-sm text-white/50">
              Manage your customers and track conversions
            </p>
          </div>
          <div className="flex items-center gap-2">
            {userEmail === 'andrew@claw9.ai' && (
              <button
                onClick={() => onOpenMessagingChannels?.()}
                className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg font-medium transition-all"
              >
                <MessageSquare className="w-4 h-4" />
                Messaging Channels
              </button>
            )}
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#ff4d4d] to-[#ff6b6b] hover:shadow-lg hover:shadow-[#ff4d4d]/30 text-white rounded-lg font-medium transition-all"
            >
              <Plus className="w-4 h-4" />
              New Lead
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="flex gap-3 overflow-x-auto pb-2">
          {isLoadingStats ? (
            <div className="flex items-center gap-2 text-white/50">
              <Loader2 className="w-5 h-5 animate-spin" />
              Loading stats...
            </div>
          ) : (
            PIPELINE_STAGES.map((stage) => (
              <StatsCard
                key={stage.id}
                stage={stage.id}
                count={stats[stage.id] || 0}
                isActive={false}
              />
            ))
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="px-4 lg:px-6 py-3 bg-[#0d1424] border-b border-white/10">
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search leads..."
              className="w-full pl-9 pr-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white placeholder-white/40"
            />
          </div>

          {/* Source Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-white/40" />
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] text-white"
            >
              <option value="all" className="bg-[#0d1424]">All Sources</option>
              {Object.entries(SOURCES).map(([key, config]) => (
                <option key={key} value={key} className="bg-[#0d1424]">
                  {config.label}
                </option>
              ))}
            </select>
          </div>

          {/* Refresh */}
          <button
            onClick={() => {
              loadStats();
              loadLeads();
            }}
            disabled={isLoadingLeads}
            className="p-2 text-white/50 hover:text-white hover:bg-white/5 rounded-lg disabled:opacity-50"
          >
            <Loader2 className={`w-5 h-5 ${isLoadingLeads ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 min-h-0 overflow-x-auto overflow-y-auto p-4 lg:p-6 bg-[#050810]">
        {isLoadingLeads && leads.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#ff4d4d]" />
          </div>
        ) : (
          <div className="flex gap-4 min-w-max h-full">
            {PIPELINE_STAGES.map((stage) => (
              <KanbanColumn
                key={stage.id}
                stage={stage.id}
                leads={filterLeads(leadsByStatus[stage.id] || [])}
                onLeadClick={handleLeadClick}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <LeadDetailModal
        lead={selectedLead}
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedLead(null);
        }}
        onUpdate={handleUpdateLead}
        onNavigateToChat={handleNavigateToChat}
        currentUserEmail={userEmail}
        onRefreshLead={async (leadId) => {
          try {
            const refreshedLead = await fetchClaw9Lead(leadId, accountId);
            setSelectedLead(refreshedLead);
            setLeads((prev) => prev.map((l) => l.id === leadId ? refreshedLead : l));
          } catch (error) {
            console.error("Failed to refresh lead:", error);
          }
        }}
      />

      <CreateLeadModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreate={handleCreateLead}
      />
    </div>
  );
};

export default Claw9CRM;
